/**
 * `roles` — Settings ▸ Roles wire surface (ADR-0030).
 *
 * Procedures:
 *   - `list`     — ALL active roles (tenanted; the role/member pickers read this)
 *   - `listPage` — cursor-paginated roles for the Settings table (10 per page)
 *   - `create` — define a custom role            (permission: roles.manage)
 *   - `update` — edit a custom role              (permission: roles.manage)
 *   - `archive`— soft-delete a custom role       (permission: roles.manage)
 *   - `assign` — put a member on a role + org slot (permission: team.manage)
 *
 * The permission *catalog* (groups, areas) is static and imported directly from
 * `@flint/api-contract` by the web editor — no procedure needed.
 */
import { z } from "zod";
import { uuid, cursor } from "../_common.js";
import { permissionSchema } from "../permissions.js";

export const memberBand = z.enum(["Founder", "Operator", "Employee"]);
const permissionList = z.array(permissionSchema).max(200);

export const roleView = z.object({
  id: uuid,
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  permissions: z.array(z.string()),
  isSystem: z.boolean(),
  departmentId: uuid.nullable(),
});

// ── list (all — for pickers) ───────────────────────────────────────
export const listInput = z.object({});
export const listOutput = z.object({ roles: z.array(roleView) });

// ── listPage (cursor-paginated — the Settings table, 10/page) ──────
export const listPageInput = z.object({
  cursor,
  limit: z.number().int().min(1).max(10).default(10),
});
export const listPageOutput = z.object({
  items: z.array(roleView),
  nextCursor: cursor,
  total: z.number().int().nonnegative(),
});

// ── create ─────────────────────────────────────────────────────────
export const createInput = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(2000).nullable().optional(),
  permissions: permissionList,
  departmentId: uuid.nullable().optional(),
});
export const createOutput = roleView;

// ── update ─────────────────────────────────────────────────────────
export const updateInput = z.object({
  roleId: uuid,
  name: z.string().trim().min(1).max(80).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  permissions: permissionList.optional(),
  departmentId: uuid.nullable().optional(),
});
export const updateOutput = roleView;

// ── archive ────────────────────────────────────────────────────────
export const archiveInput = z.object({ roleId: uuid });
export const archiveOutput = z.object({ roleId: uuid, archivedAt: z.string() });

// ── assign (member → role) ─────────────────────────────────────────
export const assignInput = z.object({
  membershipId: uuid,
  roleId: uuid,
  departmentId: uuid.nullable().optional(),
  reportsToMembershipId: uuid.nullable().optional(),
  title: z.string().trim().max(80).nullable().optional(),
});
export const assignOutput = z.object({
  membershipId: uuid,
  roleId: uuid,
  role: memberBand,
});
